import { memo, type ReactNode } from "react";
import { styled } from "tamagui";
import { Check } from "@tamagui/lucide-icons";
import { Checkbox } from "../Checkbox";
import { Icon } from "../Icon";
import { RowLayout } from "../Row/RowLayout";
import { XStack, YStack } from "../Stacks";
import { Body1, Body3 } from "../Typography";
import { useListboxUsesSheet } from "./ListboxSurfaceContext";

/**
 * The option data shape the listbox renders. Owned here so Select/Combobox alias
 * it (`SelectOption` / `ComboboxOption`) instead of redeclaring it and drifting.
 * `icon` is a `ReactNode` (not an icon-key) because consumers pass a rendered
 * `<Icon/>` into the leading slot.
 */
export type ListboxOption = {
  value: string;
  label: string;
  disabled?: boolean;
  /** Leading icon/avatar rendered before the label. */
  icon?: ReactNode;
};

// `active` is the keyboard highlight, kept as a variant so it composes with the
// hover/press states rather than fighting them.
const StyledListboxOption = styled(YStack, {
  name: "ListboxOption",
  role: "option",
  justifyContent: "center",
  paddingVertical: "$sp1",
  paddingHorizontal: "$sp1.5",
  borderRadius: "$br1",
  cursor: "pointer",
  transition: "press",
  hoverStyle: {
    backgroundColor: "$surface2Hover"
  },
  pressStyle: {
    backgroundColor: "$surface2Hover"
  },
  variants: {
    active: {
      true: {
        backgroundColor: "$surface2Hover"
      },
      false: {}
    },
    disabled: {
      // Muted text + not-allowed — never opacity or a filled background, so a
      // disabled row reads as dimmed without looking selected.
      true: {
        cursor: "not-allowed",
        hoverStyle: {
          backgroundColor: "$transparent"
        },
        pressStyle: {
          backgroundColor: "$transparent"
        }
      },
      false: {}
    }
  } as const,
  defaultVariants: {
    active: false,
    disabled: false
  }
});
export type ListboxOptionProps = {
  id: string;
  index: number;
  label: string;
  selected: boolean;
  active: boolean;
  disabled?: boolean;
  /** Leading slot — icon/avatar before the label. */
  leading?: ReactNode;
  /** Trailing slot — badge/metadata after the label, outside the content
   *  column. Sits alongside the selected-check. */
  trailing?: ReactNode;
  /** A muted second line under the label; it doesn't wrap, so a popover widens to fit it. */
  secondary?: string;
  /** Render the trailing selected-check. Select shows it; Combobox reflects the
   *  selection in its input text and opts out. @default false */
  showSelectedCheck?: boolean;
  /** Multi-select row: a leading checkbox reflects `selected` (replacing the
   *  trailing check). The row press still owns the toggle. @default false */
  multiple?: boolean;
  onSelectIndex: (index: number) => void;
  onActivateIndex: (index: number) => void;
};

// A non-focusable listbox row: the trigger holds focus (aria-activedescendant
// model), so each row carries only id + aria-selected + the active highlight.
const ListboxOptionRow = ({
  id,
  index,
  label,
  selected,
  active,
  disabled = false,
  leading,
  trailing,
  secondary,
  showSelectedCheck = false,
  multiple = false,
  onSelectIndex,
  onActivateIndex
}: ListboxOptionProps) => {
  // No keyboard in the touch sheet — suppress the `active` highlight there.
  const usesSheet = useListboxUsesSheet();
  const showActive = active && !usesSheet;
  return <StyledListboxOption id={id} aria-selected={selected} aria-disabled={disabled} active={showActive} disabled={disabled}
  // Sheet rows need a fingertip-sized target; the popover keeps the denser row.
  minHeight={usesSheet ? "$si5.5" : undefined}
  // Keep focus on the trigger through the press, else mousedown blurs it and
  // the Popover closes before the click commits (also keeps disabled clicks inert).
  onMouseDown={event => event.preventDefault()} onPress={disabled ? undefined : () => onSelectIndex(index)} onPointerEnter={disabled ? undefined : () => onActivateIndex(index)} data-sentry-element="StyledListboxOption" data-sentry-component="ListboxOptionRow" data-sentry-source-file="ListboxOption.tsx">
      <RowLayout leading={multiple ? <XStack alignItems="center" gap="$sp1">
              {/* Decorative — the row owns the toggle (aria-selected); the box is
                  a visual reflection only, hidden from the a11y tree. */}
              <Checkbox checked={selected} disabled={disabled} aria-hidden pointerEvents="none" tabIndex={-1} />
              {leading}
            </XStack> : leading} primary={<Body1 flex={1} color={disabled ? "$textMuted" : "$textDefault"}>
            {label}
          </Body1>} secondary={secondary ? <Body3 color="$textSubtle" numberOfLines={1}>
              {secondary}
            </Body3> : null} primaryTrailing={showSelectedCheck && selected ? <Icon icon={Check} size="$iconMedium" color="$iconDefault" /> : null} rowTrailing={trailing} data-sentry-element="RowLayout" data-sentry-source-file="ListboxOption.tsx" />
    </StyledListboxOption>;
};
export const ListboxOption = memo(ListboxOptionRow);